/**
 * PayPalSubscriptionButton Component
 *
 * Wrapper component for PayPal Subscription Buttons SDK integration.
 * Handles subscription creation with a pre-configured PayPal Plan ID.
 * Used for recurring (annual) payments.
 *
 * @module components/subscription/PayPalSubscriptionButton
 */

import { useEffect, useRef, useCallback } from 'react';
import { PAYPAL_BUTTON_STYLE } from '../../hooks/usePayPalScript';

/**
 * PayPal subscription data returned after successful approval
 */
export interface PayPalSubscriptionData {
  subscriptionId: string;
  planId: string;
  payerEmail?: string;
  payerName?: string;
}

/**
 * Props for the PayPalSubscriptionButton component
 */
export interface PayPalSubscriptionButtonProps {
  /** PayPal Plan ID for the subscription (configured in PayPal dashboard) */
  planId: string;
  /** Callback when subscription is successfully created */
  onSuccess: (subscriptionData: PayPalSubscriptionData) => void;
  /** Callback when user cancels the subscription flow */
  onCancel: () => void;
  /** Callback when an error occurs */
  onError: (error: Error) => void;
  /** Whether the button is enabled (terms accepted) */
  enabled: boolean;
}

/**
 * Type for the PayPal global object with subscription support
 */
interface PayPalNamespace {
  Buttons: (config: PayPalSubscriptionButtonsConfig) => PayPalButtonsInstance;
}

interface PayPalSubscriptionButtonsConfig {
  style?: {
    layout?: 'vertical' | 'horizontal';
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
    shape?: 'rect' | 'pill';
    label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment' | 'subscribe';
  };
  createSubscription: (
    data: Record<string, unknown>,
    actions: PayPalSubscriptionActions
  ) => Promise<string>;
  onApprove: (
    data: PayPalSubscriptionApproveData,
    actions: PayPalSubscriptionActions
  ) => Promise<void>;
  onCancel?: (data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
}

interface PayPalSubscriptionActions {
  subscription: {
    create: (subscriptionData: PayPalSubscriptionCreateData) => Promise<string>;
    get: () => Promise<PayPalSubscriptionDetails>;
  };
}

interface PayPalSubscriptionCreateData {
  plan_id: string;
  application_context?: {
    brand_name?: string;
    user_action?: string;
    locale?: string;
    shipping_preference?: string;
  };
}

interface PayPalSubscriptionApproveData {
  subscriptionID: string;
  orderID?: string;
  payerID?: string;
}

interface PayPalSubscriptionDetails {
  id: string;
  plan_id: string;
  status: string;
  subscriber?: {
    email_address?: string;
    name?: {
      given_name?: string;
      surname?: string;
    };
  };
}

interface PayPalButtonsInstance {
  render: (container: HTMLElement | string) => Promise<void>;
  close: () => void;
}

/**
 * Get the PayPal global namespace
 */
function getPayPal(): PayPalNamespace | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).paypal || null;
}

/**
 * PayPalSubscriptionButton Component
 *
 * Renders PayPal subscription buttons when enabled and the SDK is loaded.
 * Uses a pre-configured PayPal Plan ID for recurring billing.
 */
export function PayPalSubscriptionButton({
  planId,
  onSuccess,
  onCancel,
  onError,
  enabled,
}: PayPalSubscriptionButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Create PayPal subscription with the configured plan ID
   */
  const createSubscription = useCallback(
    async (
      _data: Record<string, unknown>,
      actions: PayPalSubscriptionActions
    ): Promise<string> => {
      return actions.subscription.create({
        plan_id: planId,
        application_context: {
          brand_name: 'Budgira',
          user_action: 'SUBSCRIBE_NOW',
          locale: 'fr-FR',
          shipping_preference: 'NO_SHIPPING',
        },
      });
    },
    [planId]
  );

  /**
   * Handle successful subscription approval
   */
  const handleApprove = useCallback(
    async (
      data: PayPalSubscriptionApproveData,
      actions: PayPalSubscriptionActions
    ): Promise<void> => {
      try {
        // Get subscription details to retrieve payer info
        const details = await actions.subscription.get();

        const payerEmail = details.subscriber?.email_address;
        const payerName = details.subscriber?.name
          ? `${details.subscriber.name.given_name || ''} ${details.subscriber.name.surname || ''}`.trim()
          : undefined;

        onSuccess({
          subscriptionId: data.subscriptionID,
          planId: details.plan_id,
          payerEmail,
          payerName,
        });
      } catch (error) {
        console.error('PayPal subscription approval error:', error);
        // Even if we can't get details, the subscription was created
        onSuccess({
          subscriptionId: data.subscriptionID,
          planId: planId,
        });
      }
    },
    [onSuccess, planId]
  );

  /**
   * Handle subscription cancellation
   */
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  /**
   * Handle PayPal errors
   */
  const handleError = useCallback(
    (error: Error) => {
      console.error('PayPal subscription error:', error);
      onError(error);
    },
    [onError]
  );

  /**
   * Render PayPal subscription buttons when enabled and SDK is loaded
   */
  useEffect(() => {
    if (!enabled || !containerRef.current || !planId) {
      return;
    }

    const paypal = getPayPal();
    if (!paypal) {
      console.error('PayPal SDK not loaded');
      return;
    }

    const container = containerRef.current;
    container.innerHTML = '';

    const buttonsConfig: PayPalSubscriptionButtonsConfig = {
      style: {
        ...PAYPAL_BUTTON_STYLE,
        label: 'subscribe' as const,
      },
      createSubscription,
      onApprove: handleApprove,
      onCancel: handleCancel,
      onError: handleError,
    };

    let buttonsInstance: PayPalButtonsInstance | null = null;

    try {
      buttonsInstance = paypal.Buttons(buttonsConfig);

      if (container && buttonsInstance) {
        buttonsInstance.render(container).catch((err: Error) => {
          console.error('PayPal subscription button render error:', err);
        });
      }
    } catch (error) {
      console.error('PayPal Subscription Buttons initialization error:', error);
    }

    return () => {
      if (buttonsInstance) {
        try {
          buttonsInstance.close();
        } catch {
          // Ignore errors during cleanup
        }
      }
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [enabled, planId, createSubscription, handleApprove, handleCancel, handleError]);

  if (!enabled) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="w-full min-h-[150px]"
      data-testid="paypal-subscription-button-container"
      aria-label="Boutons d'abonnement PayPal"
    />
  );
}

export default PayPalSubscriptionButton;
